import { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Trash2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TestimonialsEditor = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [form, setForm] = useState({ author: '', text: '', rating: 5 });
  const saveTimer = useRef(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/profile`);
        setTestimonials(res.data.testimonials || []);
      } catch (err) { toast.error('Failed to load'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const autoSave = useCallback((updated) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try { await axios.put(`${API}/profile`, { testimonials: updated }); }
      catch (err) { toast.error('Failed to save'); }
    }, 500);
  }, []);

  const handleAdd = () => {
    if (!form.author || !form.text) return;
    const updated = [...testimonials, { ...form, id: Date.now().toString() }];
    setTestimonials(updated);
    autoSave(updated);
    toast.success('Testimonial added');
    setIsAddOpen(false);
    setForm({ author: '', text: '', rating: 5 });
  };

  const handleDelete = (id) => {
    const updated = testimonials.filter(t => t.id !== id);
    setTestimonials(updated);
    autoSave(updated);
    toast.success('Testimonial removed');
  };

  if (loading) {
    return <div className="max-w-2xl flex items-center justify-center py-16">
      <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>;
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Testimonials</h1>
          <p className="text-[#A1A1AA] mt-1">Add customer reviews to display on your profile</p>
        </div>
        <Button data-testid="add-testimonial-btn" onClick={() => setIsAddOpen(true)}
          className="rounded-full bg-violet-500 hover:bg-violet-600 text-white gap-2">
          <Plus className="w-4 h-4" /> Add Review
        </Button>
      </div>

      {testimonials.length === 0 ? (
        <div className="text-center py-16">
          <Star className="w-12 h-12 text-[#71717A] mx-auto mb-4" />
          <h3 className="font-outfit font-semibold text-lg text-white mb-2">No testimonials yet</h3>
          <p className="text-[#A1A1AA] mb-6">Add customer reviews to build trust</p>
          <Button onClick={() => setIsAddOpen(true)}
            className="rounded-full bg-violet-500 hover:bg-violet-600 text-white gap-2">
            <Plus className="w-4 h-4" /> Add your first review
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {testimonials.map((t) => (
            <div key={t.id} data-testid={`testimonial-${t.id}`}
              className="rounded-2xl bg-[#121212] border border-[#27272A] p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="w-4 h-4" fill={i < (t.rating || 5) ? '#FBBF24' : 'transparent'} stroke={i < (t.rating || 5) ? '#FBBF24' : '#374151'} />
                    ))}
                  </div>
                  <p className="text-white text-sm italic">"{t.text}"</p>
                  <p className="text-[#71717A] text-xs mt-2 font-medium">— {t.author}</p>
                </div>
                <button onClick={() => handleDelete(t.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 transition-colors ml-3">
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">Add Testimonial</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Add a customer review</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#EDEDED]">Customer Name</Label>
              <Input data-testid="testimonial-author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })}
                className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="Jane Doe" />
            </div>
            <div>
              <Label className="text-[#EDEDED]">Review</Label>
              <Textarea data-testid="testimonial-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
                className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white resize-none" placeholder="Great product, loved it!" rows={3} />
            </div>
            <div>
              <Label className="text-[#EDEDED]">Rating</Label>
              <div className="flex items-center gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((r) => (
                  <button key={r} type="button" data-testid={`rating-${r}`} onClick={() => setForm({ ...form, rating: r })}>
                    <Star className="w-7 h-7 transition-colors" fill={r <= form.rating ? '#FBBF24' : 'transparent'} stroke={r <= form.rating ? '#FBBF24' : '#374151'} />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setIsAddOpen(false)}
              className="bg-transparent border-[#27272A] text-white hover:bg-white/5">Cancel</Button>
            <Button data-testid="save-testimonial-btn" onClick={handleAdd} disabled={!form.author || !form.text}
              className="bg-violet-500 hover:bg-violet-600 text-white">Add Review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestimonialsEditor;
